import { Tag } from 'antd'
import CheckableTag from 'antd/es/tag/CheckableTag'
import dayjs from 'dayjs'
import { DrilldownTableConfigContextProviderProps } from 'features/drilldown/components/table/config/DrilldownTableConfigContext'
import { DrilldownTableColumnDef } from 'features/drilldown/components/table/config/DrilldownTableSchema'
import {
  DEFAULT_TABLE_PANEL_SHARED_FIELD_DATA,
  DrilldownTablePanelData,
  LINK_TEMPLATE_ASSET,
} from 'features/drilldown/components/table/DrilldownTable'
import { PaginatedRolodexTableRecordType } from 'features/drilldown/components/table/PaginatedRolodexTable'
import _ from 'lodash'
import { Maximize } from 'lucide-react'
import { FC, useMemo, useState } from 'react'
import styled, { DefaultTheme } from 'styled-components'
import { GeckoEntry } from 'types'

import { NiiDetailsModal } from './niiDetailsModal/NiiDetailsModal'

type NiiTablePanelData = DrilldownTablePanelData

const ELIGIBILITY_TAG_COLORS: Record<string, string> = {
  Eligible: 'green',
  Likely: 'blue',
  Unclear: 'gray',
  Unlikely: 'orange',
  Ineligible: 'orange',
}

export const getEligibilityColor = (
  eligibility: string,
  theme: DefaultTheme
) => {
  return ELIGIBILITY_TAG_COLORS[eligibility] ?? theme.colors.grayscale['400']
}

const COLUMN_DEFS: ReadonlyArray<DrilldownTableColumnDef> = [
  {
    title: 'Site',
    field: 'site.name',
  },
  {
    title: 'Asset',
    field: 'unit.name',
    linkTemplate: LINK_TEMPLATE_ASSET,
  },
]

const PlainTextCell = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 20rem;
`

const getMostRecentNiiMetadataEntry = (entries: ReadonlyArray<GeckoEntry>) => {
  const niiEntries = entries.filter((e) => e.type === 'unit-nii-metadata')
  if (niiEntries.length === 0) return null

  return _.maxBy(niiEntries, (e) => {
    const analysisDate = e.data?.analysis_date as string | undefined
    return analysisDate ? dayjs(analysisDate).valueOf() : 0
  })
}

const AnalysisDateCol: FC<{ rec: PaginatedRolodexTableRecordType }> = ({
  rec: { entries },
}) => {
  const niiEntry = useMemo(
    () => getMostRecentNiiMetadataEntry(entries),
    [entries]
  )

  const analysisDate = niiEntry?.data?.analysis_date as string | undefined

  return <PlainTextCell>{analysisDate}</PlainTextCell>
}

const EligibilityCol: FC<{ rec: PaginatedRolodexTableRecordType }> = ({
  rec: { entries },
}) => {
  const niiEntry = useMemo(
    () => getMostRecentNiiMetadataEntry(entries),
    [entries]
  )

  const eligibility = niiEntry?.data?.eligibility as string | undefined

  if (!eligibility) {
    return <PlainTextCell></PlainTextCell>
  }

  const color = ELIGIBILITY_TAG_COLORS[eligibility] || 'default'

  return <Tag color={color}>{eligibility}</Tag>
}

const InspectionNotesCol: FC<{ rec: PaginatedRolodexTableRecordType }> = ({
  rec: { entries },
}) => {
  const niiEntry = useMemo(
    () => getMostRecentNiiMetadataEntry(entries),
    [entries]
  )

  const inspectionNotes = niiEntry?.data?.inspection_notes as string | undefined

  return <PlainTextCell>{inspectionNotes}</PlainTextCell>
}

const JustificationCol: FC<{ rec: PaginatedRolodexTableRecordType }> = ({
  rec: { entries },
}) => {
  const niiEntry = useMemo(
    () => getMostRecentNiiMetadataEntry(entries),
    [entries]
  )

  const justification = niiEntry?.data?.justification as string | undefined

  return <PlainTextCell>{justification}</PlainTextCell>
}

const DetailsCol: FC<{ rec: PaginatedRolodexTableRecordType }> = ({
  rec: { entries },
}) => {
  const [isOpen, setOpen] = useState<boolean>(false)
  const { niiEntry, siteEntry, unitEntry } = useMemo(
    () => ({
      niiEntry: entries.find((e) => e.type === 'unit-nii-metadata'),
      siteEntry: entries.find((e) => e.type === 'site'),
      unitEntry: entries.find((e) => e.type === 'unit'),
    }),
    [entries]
  )
  return (
    <>
      <NiiDetailsModal
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        niiEntry={niiEntry ?? null}
        siteEntry={siteEntry}
        unitEntry={unitEntry}
      />
      <CheckableTag checked={isOpen} onChange={(checked) => setOpen(checked)}>
        <Maximize />
      </CheckableTag>
    </>
  )
}

export const NII_TABLE_CONFIG_PROPS: DrilldownTableConfigContextProviderProps =
  {
    extraColumns: [
      {
        id: 'analysis_date',
        name: 'Analysis Date',
        linked_types: ['unit-nii-metadata'],
        render: ({ rec }) => <AnalysisDateCol rec={rec} />,
      },
      {
        id: 'eligibility',
        name: 'Eligibility',
        linked_types: ['unit-nii-metadata'],
        render: ({ rec }) => <EligibilityCol rec={rec} />,
      },
      {
        id: 'inspection_notes',
        name: 'Inspection Notes',
        linked_types: ['unit-nii-metadata'],
        render: ({ rec }) => <InspectionNotesCol rec={rec} />,
      },
      {
        id: 'justification',
        name: 'Justification',
        linked_types: ['unit-nii-metadata'],
        render: ({ rec }) => <JustificationCol rec={rec} />,
      },
      {
        id: 'nii_details',
        name: 'Details',
        linked_types: ['unit-nii-metadata', 'site', 'unit'],
        render: ({ rec }) => <DetailsCol rec={rec} />,
      },
    ],
  }

export const DEFAULT_NII_TABLE_PANEL_DATA: NiiTablePanelData = {
  ...DEFAULT_TABLE_PANEL_SHARED_FIELD_DATA,
  title: 'NII Eligibility',
  isPinned: true,
  columns: COLUMN_DEFS,
  groupByType: 'unit',
  enabledExtraColumnIds: [
    'analysis_date',
    'eligibility',
    'inspection_notes',
    'justification',
    'nii_details',
  ],
}
